import type { EChartsCoreOption } from 'echarts/core';
import { CORES, LEGENDA } from './Grafico';

/**
 * OS MODELOS DE GRÁFICO, por FAMÍLIA de dado. Cada cartão só oferece os modelos que fazem
 * sentido para o dado dele — trocar de modelo nunca muda o número, só o jeito de ver.
 * Todos com animação de entrada, rótulos nos pontos que importam e tooltip em reais/itens.
 */

export type Formato = 'reais' | 'inteiro' | 'decimal' | 'pct';

export const fmt = (v: number | null | undefined, f: Formato): string => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  if (f === 'reais')
    return v.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: Math.abs(v) >= 1000 ? 0 : 2,
    });
  if (f === 'pct') return `${v.toFixed(1).replace('.', ',')}%`;
  if (f === 'decimal') return v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  return Math.round(v).toLocaleString('pt-BR');
};
const curtoEixo = (f: Formato) => (v: number) =>
  f === 'reais'
    ? v >= 1000
      ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
      : `R$ ${String(Math.round(v))}`
    : f === 'pct'
      ? `${String(Math.round(v))}%`
      : v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

const ANIMA = {
  animationDuration: 900,
  animationEasing: 'cubicOut' as const,
  animationDelay: (i: number) => i * 18,
};
const GRADE = { left: 8, right: 16, top: 36, bottom: 8, containLabel: true };
const EIXO_Y = (f: Formato) => ({
  type: 'value' as const,
  axisLabel: { formatter: curtoEixo(f), color: '#8e959b', fontSize: 11 },
  splitLine: { lineStyle: { color: '#edeff1' } },
});
const EIXO_X = (cats: readonly string[]) => ({
  type: 'category' as const,
  data: cats,
  axisLabel: { color: '#6b7278', fontSize: 11 },
  axisLine: { lineStyle: { color: '#d6dadd' } },
  axisTick: { show: false },
});
const TOOL = (f: Formato) => ({
  trigger: 'axis' as const,
  valueFormatter: (v: unknown) => fmt(Number(v), f),
});

/* ------------------------------------------------------------------ *
 * Família 1 — SÉRIE NO TEMPO (um valor por dia, com o período anterior)
 * ------------------------------------------------------------------ */
export type ModeloTempo = 'linha' | 'marcadores' | 'area' | 'colunas' | 'agrupadas' | 'combo';
export const MODELOS_TEMPO: readonly [ModeloTempo, string][] = [
  ['area', 'Área'],
  ['linha', 'Linha'],
  ['marcadores', 'Linha com pontos'],
  ['colunas', 'Colunas'],
  ['agrupadas', 'Colunas × anterior'],
  ['combo', 'Combinado'],
];
export function serieNoTempo(
  modelo: ModeloTempo,
  d: {
    cats: readonly string[];
    atual: readonly (number | null)[];
    anterior: readonly (number | null)[];
    nome: string;
    /** Nome da série de comparação (padrão: "Período anterior"). */
    nomeAnterior?: string;
    formato: Formato;
    /** Série da linha no combinado (ex.: ticket médio), com o formato dela. */
    linha?: { nome: string; valores: readonly (number | null)[]; formato: Formato };
  },
): EChartsCoreOption {
  const media = (() => {
    const xs = d.atual.filter((x): x is number => x !== null);
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
  })();
  const marcaMedia =
    media === null
      ? undefined
      : {
          silent: true,
          symbol: 'none',
          lineStyle: { type: 'dashed' as const, color: '#8e959b' },
          label: {
            formatter: `média ${fmt(media, d.formato)}`,
            color: '#6b7278',
            fontSize: 10,
            position: 'insideEndTop' as const,
          },
          data: [{ yAxis: media }],
        };
  const principal = {
    name: d.nome,
    data: d.atual,
    markLine: marcaMedia,
  };
  // sem nenhum ponto de comparação, a série (e a legenda "Período anterior") nem aparece
  const temAnterior = d.anterior.some((v) => v !== null);
  const base = {
    ...ANIMA,
    grid: GRADE,
    tooltip: TOOL(d.formato),
    xAxis: EIXO_X(d.cats),
    legend: { ...LEGENDA, top: 0, right: 0 },
  };
  if (modelo === 'colunas')
    return {
      ...base,
      yAxis: EIXO_Y(d.formato),
      series: [
        { ...principal, type: 'bar', barMaxWidth: 26, itemStyle: { borderRadius: [4, 4, 0, 0] } },
      ],
    };
  if (modelo === 'agrupadas')
    return {
      ...base,
      yAxis: EIXO_Y(d.formato),
      series: [
        ...(temAnterior
          ? [
              {
                name: d.nomeAnterior ?? 'Período anterior',
                type: 'bar',
                data: d.anterior,
                barMaxWidth: 16,
                itemStyle: { color: '#d6dadd', borderRadius: [3, 3, 0, 0] },
              },
            ]
          : []),
        {
          ...principal,
          type: 'bar',
          barMaxWidth: 16,
          itemStyle: { borderRadius: [3, 3, 0, 0] },
          markLine: undefined,
        },
      ],
    };
  if (modelo === 'combo' && d.linha)
    return {
      ...base,
      tooltip: { trigger: 'axis' },
      yAxis: [EIXO_Y(d.formato), { ...EIXO_Y(d.linha.formato), splitLine: { show: false } }],
      series: [
        {
          ...principal,
          type: 'bar',
          barMaxWidth: 22,
          itemStyle: { borderRadius: [4, 4, 0, 0], color: '#f2bec2' },
          tooltip: { valueFormatter: (v: unknown) => fmt(Number(v), d.formato) },
        },
        {
          name: d.linha.nome,
          type: 'line',
          yAxisIndex: 1,
          data: d.linha.valores,
          smooth: true,
          symbolSize: 6,
          lineStyle: { width: 3, color: '#cb1b2b' },
          itemStyle: { color: '#cb1b2b' },
          tooltip: {
            valueFormatter: (v: unknown) => fmt(Number(v), d.linha?.formato ?? 'decimal'),
          },
        },
      ],
    };
  return {
    ...base,
    yAxis: EIXO_Y(d.formato),
    series: [
      ...(temAnterior
        ? [
            {
              name: d.nomeAnterior ?? 'Período anterior',
              type: 'line',
              data: d.anterior,
              smooth: true,
              symbol: 'none',
              lineStyle: { width: 1.5, type: 'dashed', color: '#b6bcc1' },
              itemStyle: { color: '#b6bcc1' },
            },
          ]
        : []),
      {
        ...principal,
        type: 'line',
        smooth: true,
        symbol: modelo === 'marcadores' ? 'circle' : 'none',
        symbolSize: 7,
        lineStyle: { width: 3 },
        areaStyle:
          modelo === 'area'
            ? {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(203,27,43,0.28)' },
                    { offset: 1, color: 'rgba(203,27,43,0.02)' },
                  ],
                },
              }
            : undefined,
        label:
          modelo === 'marcadores'
            ? {
                show: d.cats.length <= 14,
                formatter: (p: { value: number }) => curtoEixo(d.formato)(p.value),
                fontSize: 10,
                color: '#4b5157',
              }
            : undefined,
      },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Família 2 — PARTES DE UM TODO (nome → valor)
 * ------------------------------------------------------------------ */
export type ModeloPartes = 'rosca' | 'pizza' | 'barras' | 'colunas' | 'funil' | 'radial';
export const MODELOS_PARTES: readonly [ModeloPartes, string][] = [
  ['rosca', 'Rosca'],
  ['pizza', 'Pizza'],
  ['barras', 'Barras'],
  ['colunas', 'Colunas'],
  ['funil', 'Pirâmide'],
  ['radial', 'Radial'],
];
export function partes(
  modelo: ModeloPartes,
  d: {
    itens: readonly { nome: string; valor: number; cor?: string }[];
    formato: Formato;
    total?: string;
  },
): EChartsCoreOption {
  const itens = [...d.itens].sort((a, b) => b.valor - a.valor);
  const total = itens.reduce((s, x) => s + x.valor, 0);
  const pctDe = (v: number) => (total ? (v / total) * 100 : 0);
  if (modelo === 'pizza' || modelo === 'rosca')
    return {
      ...ANIMA,
      tooltip: {
        trigger: 'item',
        formatter: (p: { name: string; value: number }) =>
          `${p.name}<br/><b>${fmt(p.value, d.formato)}</b> · ${fmt(pctDe(p.value), 'pct')}`,
      },
      legend: { ...LEGENDA, bottom: 0, type: 'scroll' },
      title:
        modelo === 'rosca'
          ? {
              text: d.total ?? fmt(total, d.formato),
              subtext: 'total',
              left: 'center',
              top: '36%',
              textStyle: { fontSize: 18, fontWeight: 600, color: '#191c1f' },
              subtextStyle: { color: '#8e959b' },
            }
          : undefined,
      series: [
        {
          type: 'pie',
          radius: modelo === 'rosca' ? ['52%', '76%'] : ['0%', '72%'],
          center: ['50%', '44%'],
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 2,
            borderRadius: modelo === 'rosca' ? 6 : 2,
          },
          label: {
            formatter: (p: { percent: number }) => `${p.percent.toFixed(0)}%`,
            fontSize: 11,
            color: '#33383d',
          },
          data: itens.map((x) => ({
            name: x.nome,
            value: x.valor,
            itemStyle: x.cor ? { color: x.cor } : undefined,
          })),
        },
      ],
    };
  if (modelo === 'funil')
    return {
      ...ANIMA,
      tooltip: { trigger: 'item', valueFormatter: (v: unknown) => fmt(Number(v), d.formato) },
      series: [
        {
          type: 'funnel',
          top: 8,
          bottom: 8,
          sort: 'descending',
          gap: 3,
          left: '4%',
          width: '60%',
          minSize: '8%',
          label: {
            position: 'right',
            formatter: (p: { name: string; value: number }) =>
              `${p.name}: ${fmt(p.value, d.formato)}`,
            color: '#33383d',
            fontSize: 12,
          },
          labelLine: { lineStyle: { color: '#b6bcc1' } },
          itemStyle: { borderWidth: 0 },
          data: itens.map((x, i) => ({
            name: x.nome,
            value: x.valor,
            itemStyle: { color: x.cor ?? CORES[i % CORES.length] },
          })),
        },
      ],
    };
  if (modelo === 'radial')
    return {
      ...ANIMA,
      tooltip: { trigger: 'item', valueFormatter: (v: unknown) => fmt(Number(v), d.formato) },
      polar: { radius: ['18%', '80%'] },
      angleAxis: {
        max: Math.max(...itens.map((x) => x.valor), 1) * 1.1,
        show: false,
        startAngle: 90,
      },
      radiusAxis: {
        type: 'category',
        data: itens.map((x) => x.nome),
        axisLabel: { fontSize: 10, color: '#6b7278' },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar',
          coordinateSystem: 'polar',
          roundCap: true,
          data: itens.map((x, i) => ({
            value: x.valor,
            itemStyle: { color: x.cor ?? CORES[i % CORES.length] },
          })),
          barWidth: 10,
        },
      ],
    };
  const horizontal = modelo === 'barras';
  const cats = itens.map((x) => x.nome);
  return {
    ...ANIMA,
    grid: { ...GRADE, top: 8 },
    tooltip: { trigger: 'axis', valueFormatter: (v: unknown) => fmt(Number(v), d.formato) },
    xAxis: horizontal ? EIXO_Y(d.formato) : EIXO_X(cats),
    yAxis: horizontal
      ? {
          ...EIXO_X([...cats].reverse()),
          axisLabel: { color: '#33383d', fontSize: 11, width: 150, overflow: 'truncate' },
        }
      : EIXO_Y(d.formato),
    series: [
      {
        type: 'bar',
        // uma cor só (é a mesma medida); o 1º destacado — ou a cor do item quando ele tem (programa)
        data: (horizontal ? [...itens].reverse() : itens).map((x, i) => {
          const pos = horizontal ? itens.length - 1 - i : i;
          return {
            value: x.valor,
            itemStyle: { color: x.cor ?? (pos === 0 ? '#cb1b2b' : '#e38b92') },
          };
        }),
        barMaxWidth: 22,
        itemStyle: { borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] },
        label: {
          show: true,
          position: horizontal ? 'right' : 'top',
          formatter: (p: { value: number }) => curtoEixo(d.formato)(p.value),
          fontSize: 10,
          color: '#4b5157',
        },
      },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Família 3 — VÁRIAS SÉRIES NO TEMPO (ex.: dias × programa)
 * ------------------------------------------------------------------ */
export type ModeloEmpilhado =
  'colunas_empilhadas' | 'area_empilhada' | 'barras_empilhadas' | 'linhas' | 'agrupadas';
export const MODELOS_EMPILHADO: readonly [ModeloEmpilhado, string][] = [
  ['colunas_empilhadas', 'Colunas empilhadas'],
  ['area_empilhada', 'Área empilhada'],
  ['barras_empilhadas', 'Barras empilhadas'],
  ['linhas', 'Várias linhas'],
  ['agrupadas', 'Colunas agrupadas'],
];
export function empilhado(
  modelo: ModeloEmpilhado,
  d: {
    cats: readonly string[];
    series: readonly { nome: string; valores: readonly number[]; cor?: string }[];
    formato: Formato;
  },
): EChartsCoreOption {
  const horizontal = modelo === 'barras_empilhadas';
  const pilha = modelo === 'linhas' || modelo === 'agrupadas' ? undefined : 'total';
  return {
    ...ANIMA,
    grid: GRADE,
    legend: { ...LEGENDA, top: 0, right: 0 },
    tooltip: TOOL(d.formato),
    xAxis: horizontal ? EIXO_Y(d.formato) : EIXO_X(d.cats),
    yAxis: horizontal ? EIXO_X(d.cats) : EIXO_Y(d.formato),
    series: d.series.map((s) => ({
      name: s.nome,
      type: modelo === 'linhas' || modelo === 'area_empilhada' ? 'line' : 'bar',
      stack: pilha,
      smooth: true,
      symbol: modelo === 'linhas' ? 'circle' : 'none',
      symbolSize: 6,
      areaStyle: modelo === 'area_empilhada' ? { opacity: 0.35 } : undefined,
      lineStyle: { width: modelo === 'linhas' ? 2.5 : 1 },
      barMaxWidth: modelo === 'agrupadas' ? 12 : 26,
      itemStyle: s.cor ? { color: s.cor } : undefined,
      emphasis: { focus: 'series' },
      data: s.valores,
    })),
  };
}

/* ------------------------------------------------------------------ *
 * Família 4 — CONTRA A META (valor × alvo)
 * ------------------------------------------------------------------ */
export type ModeloMeta = 'velocimetro' | 'bullet' | 'progresso';
export const MODELOS_META: readonly [ModeloMeta, string][] = [
  ['velocimetro', 'Velocímetro'],
  ['bullet', 'Bullet'],
  ['progresso', 'Barras de progresso'],
];
export function contraMeta(
  modelo: ModeloMeta,
  d: {
    itens: readonly { nome: string; valor: number }[];
    alvo: number;
    unidade: string;
    resumo: { nome: string; pct: number };
  },
): EChartsCoreOption {
  if (modelo === 'velocimetro')
    return {
      ...ANIMA,
      series: [
        {
          type: 'gauge',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          radius: '96%',
          center: ['50%', '62%'],
          progress: {
            show: true,
            width: 16,
            roundCap: true,
            itemStyle: {
              color: d.resumo.pct >= 100 ? '#1a7f55' : d.resumo.pct >= 70 ? '#b7791f' : '#cb1b2b',
            },
          },
          axisLine: { roundCap: true, lineStyle: { width: 16, color: [[1, '#edeff1']] } },
          pointer: { length: '58%', width: 5, itemStyle: { color: '#191c1f' } },
          anchor: { show: true, size: 12, itemStyle: { color: '#191c1f' } },
          axisTick: { show: false },
          splitLine: { length: 8, lineStyle: { width: 2, color: '#b6bcc1' } },
          axisLabel: {
            distance: 18,
            color: '#8e959b',
            fontSize: 10,
            formatter: (v: number) => `${String(v)}%`,
          },
          title: { offsetCenter: [0, '32%'], fontSize: 12, color: '#6b7278' },
          detail: {
            offsetCenter: [0, '8%'],
            fontSize: 26,
            fontWeight: 700,
            color: '#191c1f',
            formatter: (v: number) => `${v.toFixed(0)}%`,
          },
          data: [{ value: Math.min(100, Math.round(d.resumo.pct)), name: d.resumo.nome }],
        },
      ],
    };
  const itens = [...d.itens].sort((a, b) => b.valor - a.valor);
  const cats = itens.map((x) => x.nome).reverse();
  const vals = itens.map((x) => x.valor).reverse();
  const maximo = Math.max(d.alvo * 1.2, ...vals, 1);
  return {
    ...ANIMA,
    grid: { ...GRADE, top: 24 },
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v: unknown) => `${fmt(Number(v), 'inteiro')} ${d.unidade}`,
    },
    xAxis: {
      type: 'value',
      max: Math.ceil(maximo),
      axisLabel: { color: '#8e959b', fontSize: 11 },
      splitLine: { lineStyle: { color: '#edeff1' } },
    },
    yAxis: {
      type: 'category',
      data: cats,
      axisLabel: { color: '#33383d', fontSize: 11 },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    series: [
      ...(modelo === 'bullet'
        ? [
            {
              type: 'bar',
              data: cats.map(() => maximo),
              barWidth: 18,
              barGap: '-100%',
              itemStyle: { color: '#f5f6f7' },
              silent: true,
              tooltip: { show: false },
            },
          ]
        : []),
      {
        type: 'bar',
        name: 'Feito',
        data: vals.map((v) => ({
          value: v,
          itemStyle: { color: v >= d.alvo ? '#1a7f55' : v >= d.alvo * 0.7 ? '#b7791f' : '#cb1b2b' },
        })),
        barWidth: modelo === 'bullet' ? 8 : 14,
        barGap: '-100%',
        z: 3,
        itemStyle: { borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          color: '#4b5157',
          formatter: (p: { value: number }) => `${String(p.value)}`,
        },
        markLine: {
          symbol: 'none',
          lineStyle: { color: '#191c1f', width: 2 },
          label: { formatter: `meta ${String(d.alvo)}`, fontSize: 10, color: '#191c1f' },
          data: [{ xAxis: d.alvo }],
        },
      },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Família 5 — COMPARAR PESSOAS em várias medidas
 * ------------------------------------------------------------------ */
export type ModeloComparacao = 'radar' | 'bolhas' | 'agrupadas';
export const MODELOS_COMPARACAO: readonly [ModeloComparacao, string][] = [
  ['bolhas', 'Bolhas'],
  ['radar', 'Radar'],
  ['agrupadas', 'Colunas agrupadas'],
];
export interface Pessoa {
  nome: string;
  faturamento: number;
  cupons: number;
  ticket: number;
  itensPorCupom: number;
  produtosDiferentesPorCupom: number;
}
export function compararPessoas(
  modelo: ModeloComparacao,
  pessoas: readonly Pessoa[],
  destaque: string | null,
): EChartsCoreOption {
  const top = pessoas.slice(0, modelo === 'radar' ? 5 : 12);
  if (modelo === 'bolhas') {
    const maxFat = Math.max(...top.map((p) => p.faturamento), 1);
    return {
      ...ANIMA,
      grid: { ...GRADE, top: 16, bottom: 24 },
      tooltip: {
        trigger: 'item',
        formatter: (p: { data: [number, number, number, string] }) =>
          `<b>${p.data[3]}</b><br/>${fmt(p.data[0], 'inteiro')} cupons · ticket ${fmt(p.data[1], 'reais')}<br/>vendeu ${fmt(p.data[2], 'reais')}`,
      },
      xAxis: {
        type: 'value',
        name: 'cupons',
        nameLocation: 'middle',
        nameGap: 22,
        axisLabel: { color: '#8e959b', fontSize: 11 },
        splitLine: { lineStyle: { color: '#edeff1' } },
      },
      yAxis: {
        type: 'value',
        name: 'ticket médio',
        axisLabel: { formatter: curtoEixo('reais'), color: '#8e959b', fontSize: 11 },
        splitLine: { lineStyle: { color: '#edeff1' } },
      },
      series: [
        {
          type: 'scatter',
          data: top.map((p) => [p.cupons, p.ticket, p.faturamento, p.nome]),
          symbolSize: (v: [number, number, number]) => 12 + (v[2] / maxFat) * 38,
          itemStyle: {
            color: (p: { data: [number, number, number, string] }) =>
              destaque && p.data[3] === destaque ? '#cb1b2b' : 'rgba(26,92,158,0.55)',
            borderColor: '#fff',
            borderWidth: 1,
          },
          label: {
            show: true,
            formatter: (p: { data: [number, number, number, string] }) =>
              p.data[3].split(' ')[0] ?? '',
            position: 'top',
            fontSize: 10,
            color: '#33383d',
          },
        },
      ],
    };
  }
  const medidas: [keyof Pessoa, string][] = [
    ['ticket', 'Ticket médio'],
    ['itensPorCupom', 'Itens por cupom'],
    ['produtosDiferentesPorCupom', 'Produtos diferentes'],
    ['cupons', 'Cupons'],
    ['faturamento', 'Faturamento'],
  ];
  const max = Object.fromEntries(
    medidas.map(([k]) => [k, Math.max(...top.map((p) => Number(p[k])), 1)]),
  );
  if (modelo === 'radar')
    return {
      ...ANIMA,
      legend: { ...LEGENDA, bottom: 0, type: 'scroll' },
      tooltip: { trigger: 'item' },
      radar: {
        radius: '62%',
        center: ['50%', '46%'],
        indicator: medidas.map(([k, nome]) => ({ name: nome, max: Number(max[k]) })),
        axisName: { color: '#4b5157', fontSize: 11 },
        splitArea: { areaStyle: { color: ['#fff', '#f7f7f5'] } },
      },
      series: [
        {
          type: 'radar',
          data: top.map((p, i) => ({
            name: p.nome,
            value: medidas.map(([k]) => Number(p[k])),
            symbolSize: 4,
            lineStyle: { width: destaque === p.nome ? 3 : 1.5 },
            areaStyle: { opacity: destaque === p.nome || (!destaque && i === 0) ? 0.25 : 0.04 },
          })),
        },
      ],
    };
  return {
    ...ANIMA,
    grid: GRADE,
    legend: { ...LEGENDA, top: 0, right: 0 },
    tooltip: { trigger: 'axis' },
    xAxis: EIXO_X(top.map((p) => p.nome.split(' ')[0] ?? p.nome)),
    yAxis: [EIXO_Y('reais'), { ...EIXO_Y('decimal'), splitLine: { show: false } }],
    series: [
      {
        name: 'Ticket médio',
        type: 'bar',
        barMaxWidth: 14,
        data: top.map((p) => p.ticket),
        tooltip: { valueFormatter: (v: unknown) => fmt(Number(v), 'reais') },
      },
      {
        name: 'Itens por cupom',
        type: 'bar',
        barMaxWidth: 14,
        yAxisIndex: 1,
        data: top.map((p) => p.itensPorCupom),
      },
      {
        name: 'Produtos diferentes',
        type: 'bar',
        barMaxWidth: 14,
        yAxisIndex: 1,
        data: top.map((p) => p.produtosDiferentesPorCupom),
      },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Família 6 — QUANDO VENDE (dia da semana × hora)
 * ------------------------------------------------------------------ */
export type ModeloQuando = 'calor' | 'colunas_hora' | 'radial_hora';
export const MODELOS_QUANDO: readonly [ModeloQuando, string][] = [
  ['calor', 'Mapa de calor'],
  ['colunas_hora', 'Colunas por hora'],
  ['radial_hora', 'Relógio'],
];
const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export function quandoVende(
  modelo: ModeloQuando,
  d: {
    mapa: readonly { dow: number; hora: number; cupons: number }[];
    porHora: readonly { hora: number; cupons: number }[];
  },
): EChartsCoreOption {
  const horas = [...new Set([...d.mapa.map((x) => x.hora), ...d.porHora.map((x) => x.hora)])].sort(
    (a, b) => a - b,
  );
  const rotuloHora = horas.map((h) => `${String(h)}h`);
  if (modelo === 'calor') {
    const max = Math.max(...d.mapa.map((x) => x.cupons), 1);
    return {
      ...ANIMA,
      grid: { ...GRADE, top: 8, bottom: 58 },
      tooltip: {
        position: 'top',
        formatter: (p: { data: [number, number, number] }) =>
          `${DIAS[p.data[1]] ?? ''} às ${rotuloHora[p.data[0]] ?? ''}<br/><b>${fmt(p.data[2], 'inteiro')} cupons</b>`,
      },
      xAxis: {
        type: 'category',
        data: rotuloHora,
        splitArea: { show: true },
        axisLabel: { color: '#6b7278', fontSize: 10 },
      },
      yAxis: {
        type: 'category',
        data: DIAS,
        splitArea: { show: true },
        axisLabel: { color: '#33383d', fontSize: 11 },
      },
      visualMap: {
        min: 0,
        max,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        itemHeight: 120,
        inRange: { color: ['#fdf2f3', '#f2bec2', '#e38b92', '#cb1b2b', '#6b0c13'] },
        textStyle: { color: '#6b7278', fontSize: 10 },
      },
      series: [
        {
          type: 'heatmap',
          data: d.mapa.map((x) => [horas.indexOf(x.hora), x.dow, x.cupons]),
          label: { show: horas.length <= 16, fontSize: 9, color: '#191c1f' },
          itemStyle: { borderColor: '#fff', borderWidth: 2, borderRadius: 3 },
        },
      ],
    };
  }
  const val = horas.map((h) => d.porHora.find((x) => x.hora === h)?.cupons ?? 0);
  if (modelo === 'radial_hora')
    return {
      ...ANIMA,
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: unknown) => `${fmt(Number(v), 'inteiro')} cupons`,
      },
      polar: { radius: ['12%', '82%'] },
      angleAxis: {
        type: 'category',
        data: rotuloHora,
        startAngle: 90,
        axisLabel: { fontSize: 10, color: '#6b7278' },
      },
      radiusAxis: { axisLabel: { show: false }, splitLine: { lineStyle: { color: '#edeff1' } } },
      series: [
        {
          type: 'bar',
          coordinateSystem: 'polar',
          data: val,
          itemStyle: { color: '#cb1b2b', borderRadius: 3 },
        },
      ],
    };
  const pico = Math.max(...val);
  return {
    ...ANIMA,
    grid: GRADE,
    tooltip: {
      trigger: 'axis',
      valueFormatter: (v: unknown) => `${fmt(Number(v), 'inteiro')} cupons`,
    },
    xAxis: EIXO_X(rotuloHora),
    yAxis: EIXO_Y('inteiro'),
    series: [
      {
        type: 'bar',
        barMaxWidth: 22,
        data: val.map((v) => ({
          value: v,
          itemStyle: { color: v === pico ? '#cb1b2b' : '#e38b92', borderRadius: [4, 4, 0, 0] },
        })),
        label: { show: true, position: 'top', fontSize: 10, color: '#4b5157' },
      },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Família 7 — FAIXA DO DIA (velas: abertura, fechamento, mínimo, máximo)
 * ------------------------------------------------------------------ */
export type ModeloVelas = 'velas' | 'faixa';
export const MODELOS_VELAS: readonly [ModeloVelas, string][] = [
  ['velas', 'Velas (candle)'],
  ['faixa', 'Faixa mín–máx'],
];
export function velas(
  modelo: ModeloVelas,
  d: {
    cats: readonly string[];
    dados: readonly { abertura: number; fechamento: number; minimo: number; maximo: number }[];
  },
): EChartsCoreOption {
  if (modelo === 'faixa')
    return {
      ...ANIMA,
      grid: GRADE,
      legend: { ...LEGENDA, top: 0, right: 0 },
      tooltip: TOOL('reais'),
      xAxis: EIXO_X(d.cats),
      yAxis: EIXO_Y('reais'),
      series: [
        {
          name: 'mínimo',
          type: 'line',
          data: d.dados.map((x) => x.minimo),
          stack: 'f',
          symbol: 'none',
          lineStyle: { opacity: 0 },
        },
        {
          name: 'faixa até o máximo',
          type: 'line',
          data: d.dados.map((x) => x.maximo - x.minimo),
          stack: 'f',
          symbol: 'none',
          lineStyle: { opacity: 0 },
          areaStyle: { color: 'rgba(203,27,43,0.18)' },
          tooltip: { show: false },
        },
        {
          name: 'fechamento',
          type: 'line',
          data: d.dados.map((x) => x.fechamento),
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2.5, color: '#cb1b2b' },
        },
      ],
    };
  return {
    ...ANIMA,
    grid: GRADE,
    tooltip: {
      trigger: 'axis',
      formatter: (ps: { name: string; data: number[] }[]) => {
        const p = ps[0];
        if (!p) return '';
        const [, a, f, mi, ma] = p.data;
        return `${p.name}<br/>abriu ${fmt(a, 'reais')} · fechou ${fmt(f, 'reais')}<br/>mín ${fmt(mi, 'reais')} · máx ${fmt(ma, 'reais')}`;
      },
    },
    xAxis: EIXO_X(d.cats),
    yAxis: { ...EIXO_Y('reais'), scale: true },
    series: [
      {
        type: 'candlestick',
        data: d.dados.map((x) => [x.abertura, x.fechamento, x.minimo, x.maximo]),
        itemStyle: {
          color: '#1a7f55',
          color0: '#cb1b2b',
          borderColor: '#1a7f55',
          borderColor0: '#cb1b2b',
        },
      },
    ],
  };
}
